import { useState } from 'react';
import axios from 'axios';
import { API } from '@/App';

const OUTCOMES = [
  { id: 'interested', label: 'Interested', icon: '✓', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800' },
  { id: 'callback', label: 'Callback', icon: '↩', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 border-blue-200 dark:border-blue-800' },
  { id: 'not_interested', label: 'Not Interested', icon: '✗', color: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 border-red-200 dark:border-red-800' },
  { id: 'voicemail', label: 'Voicemail', icon: '✉', color: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-600' },
  { id: 'no_answer', label: 'No Answer', icon: '○', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 border-amber-200 dark:border-amber-800' },
  { id: 'meeting_scheduled', label: 'Meeting Set', icon: '◎', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300 border-purple-200 dark:border-purple-800' },
];

const NEXT_STEPS = [
  { id: 'follow_up_1', label: 'Follow up in 1 day', days: 1 },
  { id: 'follow_up_3', label: 'Follow up in 3 days', days: 3 },
  { id: 'follow_up_7', label: 'Follow up in 1 week', days: 7 },
  { id: 'follow_up_14', label: 'Follow up in 2 weeks', days: 14 },
  { id: 'no_follow_up', label: 'No follow-up needed', days: 0 },
];

const ACTIVITY_TYPES = [
  { id: 'call', label: 'Call' },
  { id: 'email', label: 'Email' },
  { id: 'meeting', label: 'Meeting' },
  { id: 'linkedin_message', label: 'LinkedIn' },
  { id: 'whatsapp_message', label: 'WhatsApp' },
];

export default function QuickActivityLog({ context, onClose, onSuccess }) {
  const [step, setStep] = useState(1); // 1: outcome, 2: next step, 3: done
  const [activityType, setActivityType] = useState(context?.activity_type || 'call');
  const [outcome, setOutcome] = useState('');
  const [nextStep, setNextStep] = useState('');
  const [followUpDays, setFollowUpDays] = useState(0);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [nadDate, setNadDate] = useState('');
  const [updatingNad, setUpdatingNad] = useState(false);

  const handleOutcomeSelect = (outcomeId) => {
    setOutcome(outcomeId);
    setStep(2);
  };

  const handleNextStepSelect = async (stepOption) => {
    setNextStep(stepOption.label);
    setFollowUpDays(stepOption.days);
    await submitLog(stepOption.days, stepOption.label);
  };

  const submitLog = async (days, stepLabel) => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/activities/quick-log`, {
        lead_id: context?.lead_id || null,
        opportunity_id: context?.opportunity_id || null,
        activity_type: activityType,
        outcome: OUTCOMES.find(o => o.id === outcome)?.label || outcome,
        notes,
        next_step: stepLabel || nextStep,
        follow_up_days: days !== undefined ? days : followUpDays,
      });
      setResult(res.data);
      setStep(3);
      if (onSuccess) onSuccess(res.data);
    } catch (err) {
      console.error('Quick log failed:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="quick-activity-log-modal">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 dark:border-slate-700">
          <div>
            <h3 className="text-sm font-bold text-slate-900 dark:text-white">Quick Activity Log</h3>
            {context?.lead_name && (
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{context.lead_name}</p>
            )}
          </div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700" data-testid="quick-log-close">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Steps indicator */}
        <div className="flex items-center gap-2 px-5 py-2.5 bg-slate-50 dark:bg-slate-900/50">
          {[1, 2, 3].map(s => (
            <div key={s} className="flex items-center gap-2">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                step >= s 
                  ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900' 
                  : 'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
              }`}>
                {step > s ? (
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                ) : s}
              </div>
              {s < 3 && <div className={`w-8 h-0.5 ${step > s ? 'bg-slate-900 dark:bg-white' : 'bg-slate-200 dark:bg-slate-700'}`}></div>}
            </div>
          ))}
          <span className="text-xs text-slate-500 dark:text-slate-400 ml-2">
            {step === 1 ? 'Outcome' : step === 2 ? 'Next Step' : 'Done'}
          </span>
        </div>

        <div className="p-5">
          {/* Step 1: Activity Type + Outcome */}
          {step === 1 && (
            <div className="space-y-4">
              {/* Activity type selector */}
              <div>
                <label className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2 block">Activity Type</label>
                <div className="flex flex-wrap gap-1.5">
                  {ACTIVITY_TYPES.map(type => (
                    <button
                      key={type.id}
                      onClick={() => setActivityType(type.id)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                        activityType === type.id
                          ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 border-slate-900 dark:border-white'
                          : 'bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600 hover:border-slate-400 dark:hover:border-slate-500'
                      }`}
                      data-testid={`activity-type-${type.id}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Outcome buttons */}
              <div>
                <label className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2 block">What happened?</label>
                <div className="grid grid-cols-2 gap-2">
                  {OUTCOMES.map(o => (
                    <button
                      key={o.id}
                      onClick={() => handleOutcomeSelect(o.id)}
                      className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium transition-all hover:scale-[1.02] active:scale-[0.98] ${o.color}`}
                      data-testid={`outcome-${o.id}`}
                    >
                      <span className="text-base">{o.icon}</span>
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Optional notes */}
              <div>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Quick notes (optional)..."
                  rows={2}
                  className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-400 resize-none"
                  data-testid="quick-log-notes"
                />
              </div>
            </div>
          )}

          {/* Step 2: Next Step */}
          {step === 2 && (
            <div className="space-y-3">
              <label className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide block">Schedule Next Step</label>
              <div className="space-y-2">
                {NEXT_STEPS.map(s => (
                  <button
                    key={s.id}
                    onClick={() => handleNextStepSelect(s)}
                    disabled={submitting}
                    className="w-full flex items-center justify-between px-4 py-3 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-600 hover:border-slate-300 dark:hover:border-slate-500 transition-all disabled:opacity-50"
                    data-testid={`next-step-${s.id}`}
                  >
                    <span>{s.label}</span>
                    {s.days > 0 && (
                      <span className="text-xs text-slate-400 dark:text-slate-500">+task</span>
                    )}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setStep(1)}
                className="text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
              >
                ← Back to outcome
              </button>
            </div>
          )}

          {/* Step 3: Confirmation + NAD Update */}
          {step === 3 && (
            <div className="text-center space-y-3 py-2">
              <div className="w-14 h-14 bg-emerald-100 dark:bg-emerald-900/40 rounded-full flex items-center justify-center mx-auto">
                <svg className="w-7 h-7 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900 dark:text-white">Activity Logged!</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  {OUTCOMES.find(o => o.id === outcome)?.label} · {activityType}
                </p>
              </div>
              {result?.task_created && (
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg px-3 py-2 text-xs text-blue-700 dark:text-blue-300">
                  Follow-up task created: {result.task_created.title}
                </div>
              )}

              {/* NAD Update Prompt */}
              {context?.lead_id && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-3 py-2 text-left">
                  <p className="text-xs font-semibold text-blue-700 dark:text-blue-300 mb-1.5">Update Next Action Date?</p>
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      value={nadDate}
                      onChange={(e) => setNadDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="flex-1 px-2 py-1.5 text-xs border border-blue-300 dark:border-blue-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                      data-testid="nad-update-input"
                    />
                    <button
                      onClick={async () => {
                        if (!nadDate) return;
                        setUpdatingNad(true);
                        try {
                          await axios.put(`${API}/leads/${context.lead_id}`, { next_action_date: nadDate });
                        } catch (err) { console.error('NAD update failed:', err); }
                        finally { setUpdatingNad(false); }
                      }}
                      disabled={!nadDate || updatingNad}
                      className="px-2.5 py-1.5 text-xs font-medium bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 rounded hover:bg-slate-800 dark:hover:bg-white disabled:opacity-50 transition-colors"
                      data-testid="nad-update-btn"
                    >
                      {updatingNad ? '...' : 'Set'}
                    </button>
                  </div>
                </div>
              )}

              <button
                onClick={onClose}
                className="px-6 py-2 text-sm font-medium bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-lg hover:bg-slate-700 dark:hover:bg-slate-200 transition-colors"
                data-testid="quick-log-done"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
